import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';

import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ReplaySubject, takeUntil, firstValueFrom } from 'rxjs';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-textarea';
import '@maersk-global/mds-components-core/mc-icon';
import '@maersk-global/mds-components-core/mc-notification';
import { ToastrService } from '../../services/toastr-service';
import { PartsManagementService } from '../../services/parts-management.service';
import { CreatePartRequest } from '../../models/create-part-request.model';
import { PartsManagementResponse } from '../../models/parts-management-response.model';
import { CreatePartDetailsComponent } from '../../components/create-part/create-part-details/create-part-details.component';
import { CreatePartsOperationalSettingsComponent } from '../../components/create-part/create-part-operational-settings/create-part-operational-settings.component';
import { CreatePartsRemarksComponent } from '../../components/create-part/create-part-remarks/create-part-remarks.component';
import { ButtonWithConfirmationComponent } from '../../shared-components/button-with-confirmation/button-with-confirmation.component';

// Controls that stay editable while an active part has a zero cost, so the user can resolve it
// by entering an amount or setting the part inactive.
const ZERO_COST_RESOLUTION_CONTROLS = new Set(['price', 'isActive']);

// An empty amount is handled by the required validator, so only an actual 0 counts here.
const isZeroAmount = (price: unknown): boolean => price !== null && price !== undefined && price !== '' && Number(price) === 0;

// Only an active part must have a cost. A 0 entered by the user locks the form; a 0 already
// saved on an existing part leaves it editable but blocks making it active.
export const zeroCostPartValidator = (group: AbstractControl): ValidationErrors | null => {
  const priceControl = group.get('price');
  if (!isZeroAmount(priceControl?.value) || group.get('isActive')?.value !== true) return null;
  return priceControl?.dirty ? { zeroCostPart: true } : { zeroCostActivation: true };
};

@Component({
  selector: 'app-create-part',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    CreatePartDetailsComponent,
    CreatePartsOperationalSettingsComponent,
    CreatePartsRemarksComponent,
    NgxSkeletonLoaderModule,
    ButtonWithConfirmationComponent,
  ],
  templateUrl: './create-part.component.html',
  styleUrls: ['./create-part.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CreatePartComponent implements OnInit, OnDestroy {
  onCancel(): () => void {
    return () => {
      if (this.isEditMode()) {
        this.router.navigate(['/master-data/parts-management', this.partCode]);
      } else {
        this.router.navigate(['/master-data/parts-management']);
      }
    };
  }

  // ================= Signals =================
  formSubmitErrors = signal<string[]>([]);
  submitLoader = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  partData = signal<PartsManagementResponse | null>(null);
  isZeroCostPart = signal<boolean>(false);

  private readonly destroyed$ = new ReplaySubject<boolean>(1);
  partCode: string | null = null;

  // ================= Form =================
  createPartForm = this.formBuilder.group(
    {
      partCode: this.formBuilder.nonNullable.control('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(50),
        Validators.pattern(/^[a-zA-Z0-9-]+$/),
      ]),
      partGroupCode: this.formBuilder.nonNullable.control('', Validators.required),
      description: this.formBuilder.nonNullable.control('', [Validators.required, Validators.maxLength(200)]),
      partManufacturerCode: this.formBuilder.control('', [Validators.maxLength(10), Validators.pattern(/^[a-zA-Z0-9]*$/)]),
      coreValue: this.formBuilder.control<number | null>(0, [Validators.min(0), Validators.max(999999.99)]),
      quantity: this.formBuilder.control<number | null>(0, [Validators.min(0), Validators.max(999999)]),
      price: this.formBuilder.control<number | null>(null, [Validators.required, Validators.min(0), Validators.max(999999.99)]),
      isActive: this.formBuilder.control<boolean>(false),
      deductCore: this.formBuilder.control<boolean>(false),
      isSerialTagNumberRequired: this.formBuilder.control<boolean>(false),
      remarks: this.formBuilder.control('', [Validators.maxLength(500)]),
    },
    { validators: zeroCostPartValidator },
  );

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly toastrService: ToastrService,
    private readonly partsManagementService: PartsManagementService,
  ) {}

  // ================= Init =================
  ngOnInit(): void {
    this.createPartForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => this.syncZeroCostLock());

    this.route.paramMap.pipe(takeUntil(this.destroyed$)).subscribe((params) => {
      this.partCode = params.get('id');
      if (this.partCode) {
        this.isEditMode.set(true);
        // loadPartData is async but we don't await here to keep ngOnInit synchronous
        this.loadPartData(this.partCode);
      } else {
        this.isEditMode.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  // ================= Load for Edit =================
  private async loadPartData(partCode: string): Promise<void> {
    try {
      const data = await firstValueFrom(this.partsManagementService.getPartsManagementById(partCode));

      this.partData.set(data);
      this.populateForm(data);
    } catch (error) {
      // log the error for diagnostics and then show user-friendly message
      // (don't swallow the exception silently)
      // eslint-disable-next-line no-console
      console.error('Failed to load part data', error);
      this.toastrService.showToastr('Failed to load part data', 'error');
      this.router.navigate(['/master-data/parts-management']);
    }
  }

  private populateForm(data: PartsManagementResponse): void {
    this.createPartForm.patchValue({
      partCode: data.partCode,
      partGroupCode: data.partGroupCode,
      description: data.description,
      partManufacturerCode: data.partManufacturerCode,
      coreValue: data.coreValue,
      quantity: data.quantity,
      price: data.price,
      isActive: data.isActive,
      deductCore: data.deductCore,
      isSerialTagNumberRequired: data.isSerialTagNumberRequired,
      remarks: data.remarks,
    });

    // Disable partCode in edit mode
    this.createPartForm.get('partCode')?.disable();

    this.syncZeroCostLock();
    this.createPartForm.markAsPristine();
  }

  // ================= Zero Cost Lock =================
  // An active part must not have a zero cost. While it does, lock every other field
  // until the user enters an amount or sets the part inactive.
  private syncZeroCostLock(): void {
    const locked = this.createPartForm.hasError('zeroCostPart');
    if (locked === this.isZeroCostPart()) return;
    this.isZeroCostPart.set(locked);

    for (const [name, control] of Object.entries(this.createPartForm.controls)) {
      if (ZERO_COST_RESOLUTION_CONTROLS.has(name)) continue;
      if (name === 'partCode' && this.isEditMode()) continue;
      if (locked) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
    this.createPartForm.updateValueAndValidity({ emitEvent: false });
  }

  // ================= Submit =================
  onSubmit(): void {
    // The zero cost warning banners already explain why the form cannot be saved.
    if (this.createPartForm.hasError('zeroCostPart') || this.createPartForm.hasError('zeroCostActivation')) {
      return;
    }

    if (this.createPartForm.invalid) {
      this.createPartForm.markAllAsTouched();
      this.toastrService.showToastr('Please fill in all required fields correctly', 'warning');
      return;
    }

    if (this.isEditMode()) {
      this.updatePart();
    } else {
      this.createPart();
    }
  }

  // ================= Create =================
  private createPart(): void {
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);

    const request = this.buildRequest(this.createPartForm.getRawValue());

    this.partsManagementService
      .createPart(request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.submitLoader.set(false);
          this.toastrService.showToastr(`Part '${response.partCode}' created successfully`, 'success');
          this.router.navigate(['/master-data/parts-management']);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'create');
        },
      });
  }

  // ================= Update =================
  private updatePart(): void {
    this.isEditMode.set(true);
    this.submitLoader.set(true);
    this.formSubmitErrors.set([]);

    const request = this.buildRequest(this.createPartForm.getRawValue());

    this.partsManagementService
      .updatePart(this.partCode!, request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.submitLoader.set(false);
          this.toastrService.showToastr(`Part '${this.partCode}' updated successfully`, 'success');
          this.router.navigate(['/master-data/parts-management', this.partCode]);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'update');
        },
      });
  }

  // ================= Request Builder =================
  private buildRequest(formValue: any): CreatePartRequest {
    return {
      partCode: formValue.partCode,
      partGroupCode: formValue.partGroupCode,
      description: formValue.description,
      partManufacturerCode: formValue.partManufacturerCode || undefined,
      coreValue: formValue.coreValue ?? 0,
      quantity: formValue.quantity ?? 0,
      price: formValue.price ?? 0,
      isActive: formValue.isActive ?? false,
      deductCore: formValue.deductCore ?? false,
      isSerialTagNumberRequired: formValue.isSerialTagNumberRequired ?? false,
      remarks: formValue.remarks || undefined,
    };
  }

  // ================= Error Handler =================
  private handleError(error: HttpErrorResponse, operation: string): void {
    this.submitLoader.set(false);

    let errorMessages: string[];
    if (error.status === 409) {
      errorMessages = ['Part number already exists.'];
    } else if (error.status === 422 && error.error?.violations) {
      errorMessages = error.error.violations.map((v: { message: string }) => v.message);
    } else {
      errorMessages = ['Failed to create part. Please try again.'];
    }

    this.formSubmitErrors.set(errorMessages);

    this.toastrService.showToastr(`Error creating part`, 'error');
  }
}
